import { useCallback, useEffect, useState } from "react";
import {
  Alert, Avatar, Button, Drawer, Empty, Form, Input, List, Space, Tabs, Tag, Tooltip, Typography, message,
} from "antd";
import {
  listInstances, listComments, createComment, listAudit, approveTask, rejectTask,
} from "../../apiservice/workflow";

const { Text, Paragraph } = Typography;

/**
 * Reusable workflow drawer. Mount on any record page:
 *
 *   <WorkflowDrawer
 *     recordType="quotation"
 *     recordId={42}
 *     open={drawerOpen}
 *     onClose={() => setDrawerOpen(false)}
 *   />
 *
 * Shows: instance status (tasks + state), discussion (comments),
 *        audit trail (chronological history).
 */
export default function WorkflowDrawer({ recordType, recordId, open, onClose, title }) {
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!recordType || !recordId) return;
    setLoading(true);
    try {
      const r = await listInstances(recordType, recordId);
      setInstances(r?.data?.data || r?.data || []);
    } catch (e) {
      message.error(e.message || "Load failed");
    } finally { setLoading(false); }
  }, [recordType, recordId]);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  return (
    <Drawer
      title={title || `Workflows — ${recordType} #${recordId}`}
      open={open}
      onClose={onClose}
      width={620}
      destroyOnClose
      bodyStyle={{ paddingRight: 16, paddingLeft: 16, paddingBottom: 16 }}
    >
      <Tabs
        items={[
          { key: "instances", label: "Status", children: <InstancesPanel instances={instances} loading={loading} onChanged={refresh} /> },
          { key: "comments", label: "Discussion", children: <CommentsPanel recordType={recordType} recordId={recordId} /> },
          { key: "audit", label: "Audit log", children: <AuditPanel recordType={recordType} recordId={recordId} /> },
        ]}
      />
    </Drawer>
  );
}

function InstancesPanel({ instances, loading, onChanged }) {
  if (loading) return <div>Loading…</div>;
  if (!instances.length) return <Empty description="No workflows have run for this record yet." />;
  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      {instances.map(({ workflow, tasks }) => (
        <InstanceCard key={workflow.id} workflow={workflow} tasks={tasks} onChanged={onChanged} />
      ))}
    </Space>
  );
}

function InstanceCard({ workflow, tasks, onChanged }) {
  const pending = tasks.filter((t) => t.status === "Pending");
  const completed = tasks.filter((t) => t.status === "Completed" || t.status === "Approved");
  const rejected = tasks.filter((t) => t.status === "Rejected" || t.status === "Cancelled");

  let overall;
  if (rejected.length) overall = <Tag color="red">Rejected</Tag>;
  else if (pending.length) overall = <Tag color="orange">In progress · {pending.length} pending</Tag>;
  else overall = <Tag color="green">Completed</Tag>;

  return (
    <div style={{ border: "1px solid #eee", borderRadius: 6, padding: 12 }}>
      <Space style={{ width: "100%", justifyContent: "space-between" }}>
        <Text strong>{workflow.name}</Text>
        {overall}
      </Space>
      <div style={{ marginTop: 8, fontSize: 12, color: "#888" }}>
        Trigger: <Text code>{workflow.trigger_event || "manual"}</Text>
      </div>
      <List
        size="small"
        style={{ marginTop: 8 }}
        dataSource={tasks}
        renderItem={(t) => (
          <List.Item
            actions={t.status === "Pending" ? [
              <ApproveButton key="a" task={t} onChanged={onChanged} />,
              <RejectButton key="r" task={t} onChanged={onChanged} />,
            ] : []}
          >
            <Space>
              <TaskStatusBadge status={t.status} />
              <Text code>{t.node_id}</Text>
              {t.action_type && <Tag>{t.action_type}</Tag>}
              {t.assigned_to_user && <Tooltip title="Assigned user id"><Tag color="blue">user #{t.assigned_to_user}</Tag></Tooltip>}
              {t.assigned_to_role && <Tag color="purple">role #{t.assigned_to_role}</Tag>}
              {t.due_at && <Tag color={new Date(t.due_at) < new Date() ? "red" : "default"}>due {new Date(t.due_at).toLocaleString()}</Tag>}
            </Space>
          </List.Item>
        )}
      />
    </div>
  );
}

function TaskStatusBadge({ status }) {
  const map = {
    Created: ["default", "Created"],
    Pending: ["orange", "Pending"],
    Approved: ["green", "Approved"],
    Rejected: ["red", "Rejected"],
    Completed: ["green", "Done"],
    Cancelled: ["default", "Cancelled"],
  };
  const [color, label] = map[status] || ["default", status];
  return <Tag color={color}>{label}</Tag>;
}

function ApproveButton({ task, onChanged }) {
  const [busy, setBusy] = useState(false);
  return (
    <Button
      size="small" type="primary"
      loading={busy}
      onClick={async () => {
        setBusy(true);
        try { await approveTask(task.id, null); message.success("Approved"); onChanged(); }
        catch (e) { message.error(e.message); }
        finally { setBusy(false); }
      }}
    >Approve</Button>
  );
}

function RejectButton({ task, onChanged }) {
  const [busy, setBusy] = useState(false);
  return (
    <Button
      size="small" danger
      loading={busy}
      onClick={async () => {
        const remarks = window.prompt("Reason for rejection (optional)?");
        if (remarks === null) return;
        setBusy(true);
        try { await rejectTask(task.id, remarks || null); message.success("Rejected"); onChanged(); }
        catch (e) { message.error(e.message); }
        finally { setBusy(false); }
      }}
    >Reject</Button>
  );
}

function CommentsPanel({ recordType, recordId }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form] = Form.useForm();

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listComments(recordType, recordId);
      setComments(r?.data?.data || r?.data || []);
    } catch (e) {
      message.error(e.message || "Load failed");
    } finally { setLoading(false); }
  }, [recordType, recordId]);
  useEffect(() => {
    refresh();
  }, [refresh]);

  async function onSubmit(values) {
    try {
      await createComment({
        record_type: recordType,
        record_id: recordId,
        body: values.body,
        is_internal: !!values.is_internal,
      });
      form.resetFields();
      refresh();
    } catch (e) { message.error(e.message); }
  }

  return (
    <div>
      <List
        loading={loading}
        dataSource={comments}
        locale={{ emptyText: "No comments yet." }}
        renderItem={(c) => (
          <List.Item>
            <List.Item.Meta
              avatar={<Avatar>{String(c.user_id || "U").slice(-2)}</Avatar>}
              title={
                <Space>
                  <Text strong>User #{c.user_id}</Text>
                  {c.is_internal ? <Tag color="orange">internal</Tag> : null}
                  <Text type="secondary" style={{ fontSize: 12 }}>{new Date(c.created_at).toLocaleString()}</Text>
                </Space>
              }
              description={<Paragraph style={{ marginBottom: 0, whiteSpace: "pre-wrap" }}>{c.body}</Paragraph>}
            />
          </List.Item>
        )}
      />
      <Form form={form} layout="vertical" onFinish={onSubmit} style={{ marginTop: 12 }}>
        <Form.Item name="body" rules={[{ required: true, message: "Comment cannot be empty" }]}>
          <Input.TextArea rows={3} placeholder="Add a comment…" />
        </Form.Item>
        <Space>
          <Form.Item name="is_internal" valuePropName="checked" noStyle><input type="checkbox" id="internal-cb" /></Form.Item>
          <label htmlFor="internal-cb" style={{ fontSize: 12, color: "#666" }}>Internal note (not visible to customer)</label>
          <Button type="primary" htmlType="submit">Post</Button>
        </Space>
      </Form>
    </div>
  );
}

function AuditPanel({ recordType, recordId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await listAudit(recordType, recordId);
        setRows(r?.data?.data || r?.data || []);
      } catch (e) {
        message.error(e.message || "Load failed");
      } finally { setLoading(false); }
    })();
  }, [recordType, recordId]);

  if (loading) return <div>Loading…</div>;
  if (!rows.length) return <Empty description="No audit events yet." />;

  return (
    <List
      dataSource={rows}
      renderItem={(r) => (
        <List.Item>
          <Space direction="vertical" size={2} style={{ width: "100%" }}>
            <Space>
              <Tag>{r.event}</Tag>
              {r.from_state || r.to_state ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {r.from_state || "—"} → {r.to_state || "—"}
                </Text>
              ) : null}
              <Text type="secondary" style={{ fontSize: 12 }}>{new Date(r.created_at).toLocaleString()}</Text>
            </Space>
            {r.actor_user_id && <Text type="secondary" style={{ fontSize: 12 }}>by user #{r.actor_user_id}</Text>}
            {r.detail && <Text code style={{ fontSize: 11 }}>{typeof r.detail === "string" ? r.detail : JSON.stringify(r.detail)}</Text>}
          </Space>
        </List.Item>
      )}
    />
  );
}
